import React, { useState, useRef, useEffect, useCallback } from 'react';
import { useDispatch } from "react-redux";
import { useNavigate, useLocation } from "react-router-dom";
import { notify } from '../../services/notification';
import Header from "../../component/loaderComponent/Header/Header";
import AddContentSkeleton from '../../component/SkeletonLoader/AddContentSkeleton';
import { getVehiclesAddContentAction, createContentAction, updateContentAction, getVehiclesCategoryAction, getContentDetails, getUserTypeListAction, getTrainingCategoryAction, getTypeMasterListAction } from '../../actions/trainingModuleAction';
import './addcontent.scss';
import {
    FormControl,
    InputLabel,
    Select,
    Checkbox,
    ListItemText,
    MenuItem,
    Button,
    Tooltip,
} from "@mui/material";
import { HTTP_STATUS } from '../../constants/httpsStatus/httpsStatus';
import { FaTrash } from 'react-icons/fa';
import useMobileView from "../../constants/useMobileView";
import { vehicleType } from '../../constants/constant';
import { is2w } from '../../constants/vehicleTypeHelper';

const AddContent = () => {
    const twoW = is2w();

    const { search } = useLocation();
    const param = new URLSearchParams(search);
    const id = param.get("id");
    const [pageLoading, setPageLoading] = useState(true);
    const [submitting, setSubmitting] = useState(false);
    const [vehicles, setVehicles] = useState([]);
    const [vehiclesCategory, setVehiclesCategory] = useState([]);
    const [userTypes, setUserTypes] = useState([]);
    const [categoryOptions, setCategoryOptions] = useState([]);
    const [contentTypeOptions, setContentTypeOptions] = useState([]);
    const { isSidebarOpen, toggleSidebar } = useMobileView();
    const navigate = useNavigate();
    const dispatch = useDispatch();

    const [state, setState] = useState({
        description: "",
        type: "",
        vehicle_ids: [],
        title: "",
        attachment_type: "",
        attachments: {},
        attachment: "",
        file_name: "",
        ...(twoW ? { category: "", user_types: [] } : { vehicle_category: "" }),
    });

    const contentListPath = twoW ? '/2w/training/list' : '/training/tutorialtroubleshooting';

    const fetchVehicles = useCallback(async (categoryParam) => {
        try {
            const response = await dispatch(getVehiclesAddContentAction(categoryParam));
            if (response?.status === HTTP_STATUS.SUCCESS) {
                setVehicles(response?.data?.data?.results || []);
            }
        } catch (error) {
            notify.error('Failed to load vehicles');
        }
    }, [dispatch]);

    useEffect(() => {
        const initPage = async () => {
            setPageLoading(true);
            try {
                if (twoW) {
                    const promises = [
                        dispatch(getTrainingCategoryAction()),
                        dispatch(getUserTypeListAction()),
                        dispatch(getTypeMasterListAction()),
                        dispatch(getVehiclesCategoryAction()),
                    ];
                    if (id) promises.push(dispatch(getContentDetails(id)));

                    const [categoriesResp, userTypesResp, contentTypesResp, vehicleCatResp, editResp] = await Promise.all(promises);

                    const categories = categoriesResp?.data?.data || [];
                    setCategoryOptions(categories.map(cat => ({ value: cat.id, label: cat.display_name })));
                    setUserTypes(userTypesResp?.data?.data?.results || userTypesResp?.data?.data || []);
                    setContentTypeOptions((contentTypesResp?.data?.data || []).map(t => ({ value: t.slug, label: t.display_name })));

                    const vehicleCategories2W = vehicleCatResp?.data?.data?.results || [];
                    const twoWheelerId = vehicleCategories2W.find(item => item?.name === "2 Wheeler")?.id;
                    if (twoWheelerId) await fetchVehicles(twoWheelerId);

                    if (id && editResp) {
                        const data = editResp?.data?.data ?? editResp?.data ?? {};
                        const categoryId = categories.find(cat => cat.display_name === data?.training_category)?.id ?? '';
                        setState(prev => ({
                            ...prev,
                            title: data?.title ?? '',
                            description: data?.description ?? '',
                            vehicle_ids: data?.vehicle_list?.map(item => item?.vehicle_id) ?? [],
                            type: data?.type ?? '',
                            attachment_type: data?.attachment_type?.toLowerCase() ?? '',
                            file_name: data?.file_name ?? '',
                            category: categoryId,
                            user_types: data?.user_types?.map(item => item?.id ?? item) ?? [],
                        }));
                    }
                } else {
                    const promises = [
                        dispatch(getVehiclesCategoryAction()),
                        dispatch(getTypeMasterListAction()),
                    ];
                    if (id) promises.push(dispatch(getContentDetails(id)));

                    const [vehicleCatResp, contentTypesResp, editResp] = await Promise.all(promises);

                    const vehicleCategories = vehicleCatResp?.data?.data?.results || [];
                    setVehiclesCategory(vehicleCategories);
                    setContentTypeOptions((contentTypesResp?.data?.data || []).map(t => ({ value: t.slug, label: t.display_name })));

                    if (id && editResp) {
                        const data = editResp?.data?.data ?? editResp?.data ?? {};
                        const vehicle_category = data?.vehicle_list?.[0]?.vehicle_category_id ?? '';
                        if (vehicle_category) await fetchVehicles(vehicle_category);
                        setState(prev => ({
                            ...prev,
                            title: data?.title ?? '',
                            description: data?.description ?? '',
                            vehicle_ids: data?.vehicle_list?.map(item => item?.vehicle_id) ?? [],
                            type: data?.type ?? '',
                            attachment_type: data?.attachment_type?.toLowerCase() ?? '',
                            file_name: data?.file_name ?? '',
                            vehicle_category,
                        }));
                    } else {
                        const defaultId = vehicleCategories.find(item => item?.name === vehicleType.threeWheeler)?.id;
                        if (defaultId) setState(prevState => ({ ...prevState, vehicle_category: defaultId }));
                        await fetchVehicles(2);
                    }
                }
            } finally {
                setPageLoading(false);
            }
        };
        initPage();
    }, []);

    const handleCategoryChange = (e) => {
        if (twoW) {
            const selectedCategory = e.target.value;
            setState(prevState => ({
                ...prevState,
                category: selectedCategory,
                vehicle_ids: []
            }));
        } else {
            const selectedCategoryId = e.target.value;
            setState(prevState => ({
                ...prevState,
                vehicle_category: selectedCategoryId,
                vehicle_ids: selectedCategoryId ? prevState?.vehicle_ids : []
            }));
        }
    };

    const handleInputChange = useCallback((e) => {
        const { name, value } = e.target;
        setState(prev => ({
            ...prev,
            [name]: value,
            ...(name === "attachment_type" ? { attachments: null, file_name: "" } : {})
        }));
    }, []);

    const handleFileChange = useCallback((e) => {
        const file = e.target.files[0];

        if (state?.attachment_type.toLowerCase() === 'pdf' && file?.type !== 'application/pdf') {
            notify.error('Only PDF files are allowed for PDF type');
            return;
        } else if (state?.attachment_type.toLowerCase() === 'video' && !file?.type.startsWith('video/')) {
            notify.error('Only video files are allowed for video type');
            return;
        }

        setState(prevState => ({
            ...prevState,
            attachments: file,
            file_name: file.name,
        }));
    }, [state.attachment_type]);

    const handleUserTypeChange = (e) => {
        const value = e.target.value;
        if (value.includes("all")) {
            setState(prev => ({
                ...prev,
                user_types: value.length === userTypes.length + 1 ? [] : userTypes.map(ut => ut.id),
            }));
        } else {
            setState(prev => ({
                ...prev,
                user_types: value,
            }));
        }
    };

    const handleOnSaveClick = async (e) => {
        e.preventDefault();
        setSubmitting(true);

        const commonValid = state?.title && state?.description && state?.attachments;
        console.log(state?.category , state?.type)
        const typeValid = twoW
            ? (state?.category && state?.type)
            : (state?.vehicle_ids?.length);
            console.log(!typeValid, !commonValid)
        if (!commonValid || !typeValid) {
            notify.error('Please fill all the fields');
            setSubmitting(false);
            return;
        }

        const formData = new FormData();
        formData.append('description', state?.description);
        formData.append('type', state?.type);
        state?.vehicle_ids.forEach((id) => {
            formData.append('vehicle_ids', [id]);
        });
        formData.append('title', state?.title);
        formData.append('attachments.attachment_type', state?.attachment_type);
        formData.append('training_category_id', twoW ? state?.category : 4);

        if (!twoW) {
            formData.append('vehicle_category', state?.vehicle_category);
        }

        if (state?.attachments) {
            formData.append('attachments.attachment', state?.attachments);
        } else {
            notify.error('Attachment is required');
            setSubmitting(false);
            return;
        }
        try {
            const response = await dispatch(createContentAction(formData));

            if (response?.status === HTTP_STATUS.SUCCESS || response?.status === HTTP_STATUS.CREATED) {
                notify.success(response?.data?.message);
                setSubmitting(false);
                navigate(contentListPath);
            } else {
                setSubmitting(false);
            }
        } catch (error) {
            setSubmitting(false);
            notify.error('Failed to save content');
        }
    };

    const handleOnUpdateClick = async (e) => {
        e.preventDefault();
        setSubmitting(true);

        const commonValid = state?.title && state?.description;
        const typeValid = twoW
            ? (state?.category && state?.type)
            : (state?.vehicle_ids?.length);
        const hasFile = !!state.file_name || !!state.attachments?.name;
        if (!commonValid || !typeValid || !hasFile) {
            notify.error('Please fill all the fields');
            setSubmitting(false);
            return;
        }
        const formData = new FormData();
        formData.append('description', state?.description);
        formData.append('type', state?.type);
        state?.vehicle_ids.forEach((id) => {
            formData.append('vehicle_ids', [id]);
        });
        formData.append('title', state?.title);
        formData.append('training_category_id', twoW ? state?.category : 4);

        if (twoW) {
            state?.user_types.forEach((ut) => {
                formData.append('user_types', [ut]);
            });
        } else {
            formData.append('vehicle_category', state?.vehicle_category);
        }

        if (state?.attachments?.name) {
            formData.append('attachments.attachment_type', state?.attachment_type);
            formData.append('attachments.attachment', state?.attachments);
        }

        try {
            const response = await dispatch(updateContentAction(formData, id));

            if (response?.status === HTTP_STATUS.SUCCESS || response?.status === HTTP_STATUS.CREATED) {
                notify.success(response?.data?.message);
                setSubmitting(false);
                navigate(contentListPath);
            } else {
                setSubmitting(false);
                notify.error('Failed to save content');
            }
        } catch (error) {
            setSubmitting(false);
            notify.error('Failed to save content');
        }
    };

    const handleDeleteFile = () => {
        setState(prevState => {
            return {
                ...prevState,
                file_name: "",
                attachments: {}
            };
        });
    };

    const isFormValid = () => {
        const hasFile = !!state.file_name || !!state.attachments?.name;
        if (id) {
            if (twoW) return !!(state.category && state?.title && state?.description && hasFile);
            return !!(state?.title && state?.description && state?.vehicle_ids?.length > 0 && state?.vehicle_category && hasFile);
        }
        if (twoW) {
            return !!(state.category && state?.title && state?.description && state?.type && state?.attachments && state?.file_name && state?.attachment_type);
        }
        return !!(state?.title && state?.description && state?.type && state?.vehicle_ids?.length > 0 && state?.attachments && state?.file_name && state?.attachment_type && state?.vehicle_category);
    };

    return (
        <div className={twoW ? 'training-add-content-2w' : 'training-add-content'}>
            <div className="create-notification-excel">
                <div className="main-content">
                    <Header title={id ? "Edit Content" : "Add Content"} />
                    {pageLoading && <AddContentSkeleton is2w={twoW} />}
                    {!pageLoading && <div className={`form-container${submitting ? ' form-container--saving' : ''}`}>
                        <form
                            onSubmit={id ? handleOnUpdateClick : handleOnSaveClick}
                            className="form-div"
                            style={submitting ? { pointerEvents: 'none', opacity: 0.7 } : undefined}
                        >
                            {twoW ? (
                                <div className="form-group">
                                    <label className="form-label">Select Category</label>
                                    <select
                                        name="category"
                                        className="form-select-dropdown"
                                        value={state.category}
                                        onChange={handleCategoryChange}
                                        required
                                    >
                                        <option value="">Select Category</option>
                                        {categoryOptions.map((opt) => (
                                            <option key={opt.value} value={opt.value}>{opt.label}</option>
                                        ))}
                                    </select>
                                </div>
                            ) : (
                                <div className="form-group">
                                    <label className="form-label">Select Vehicle Category</label>
                                    <select
                                        name="vehicle_category"
                                        className="add-content-select-input"
                                        value={state.vehicle_category}
                                        onChange={handleCategoryChange}
                                        required
                                        disabled
                                    >
                                        <option selected={state.vehicle_category} value="">Select a category</option>
                                        {vehiclesCategory && vehiclesCategory.map((vehicle) => (
                                            <option key={vehicle.id} value={vehicle.id}>{vehicle.name}</option>
                                        ))}
                                    </select>
                                </div>
                            )}

                            <div className="form-group">
                                <label className={`form-label${twoW ? ' form-label-optional' : ''}`}>
                                    Select Vehicle
                                </label>
                                <FormControl className="multiselect_part" fullWidth>
                                    <InputLabel className={`multiselect-label${twoW ? '' : ' required'}`} htmlFor="vehicle">Select Vehicle</InputLabel>
                                    <Select
                                        className='multi-select-box'
                                        multiple
                                        value={state?.vehicle_ids}
                                        onChange={(e) => {
                                            const value = e.target.value;
                                            if (value.includes("all")) {
                                                setState(prev => ({
                                                    ...prev,
                                                    vehicle_ids: value?.length === vehicles?.length + 1 ? [] : vehicles.map(vehicle => vehicle?.id),
                                                }));
                                            } else {
                                                setState(prev => ({
                                                    ...prev,
                                                    vehicle_ids: value,
                                                }));
                                            }
                                        }}
                                        renderValue={(selected) => {
                                            const selectedNames = vehicles
                                                .filter((vehicle) => selected.includes(vehicle?.id))
                                                .map((vehicle) => vehicle?.name);
                                            const displayText = selectedNames.join(", ");
                                            return (
                                                <Tooltip
                                                    title={selectedNames.length > 1 ? displayText : ''}
                                                    placement="top"
                                                >
                                                    <span style={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', display: 'block' }}>
                                                        {displayText}
                                                    </span>
                                                </Tooltip>
                                            );
                                        }}
                                    >
                                        {vehicles?.length > 0 && (
                                            <MenuItem value="all">
                                                <Checkbox checked={state?.vehicle_ids?.length === vehicles?.length} />
                                                <ListItemText primary="Select All" />
                                            </MenuItem>
                                        )}
                                        {vehicles?.map((vehicle) => (
                                            <MenuItem key={vehicle?.id} value={vehicle?.id}>
                                                <Checkbox checked={state?.vehicle_ids.includes(vehicle?.id)} />
                                                <ListItemText primary={vehicle?.name} />
                                            </MenuItem>
                                        ))}
                                    </Select>
                                </FormControl>
                            </div>

                            <div className="form-group">
                                <label className="form-label">
                                    Select Type of Content 
                                </label>
                                <select
                                    name="type"
                                    className="form-select-dropdown"
                                    value={state.type}
                                    onChange={handleInputChange}
                                    required
                                >
                                    <option disabled value="">Select Type of Content</option>
                                    {contentTypeOptions.map((opt) => (
                                        <option key={opt.value} value={opt.value}>{opt.label}</option>
                                    ))}
                                </select>
                            </div>

                            <div className="form-group">
                                <label className="form-label">Content Title</label>
                                <div>
                                    <textarea
                                        name="title"
                                        className="form-textarea"
                                        placeholder="Content Title"
                                        value={state.title}
                                        onChange={handleInputChange}
                                        maxLength="100"
                                        required
                                    />
                                    <span className="textcount-notyfy-excel-headings">{state?.title?.length}/100</span>
                                </div>
                            </div>

                            <div className="form-group">
                                <label className="form-label">Description</label>
                                <div>
                                    <textarea
                                        name="description"
                                        className="form-textarea"
                                        placeholder="Description"
                                        value={state.description}
                                        onChange={handleInputChange}
                                        maxLength="500"
                                        required
                                    />
                                    <span className="textcount-notyfy-excel-description">{state?.description?.length}/500</span>
                                </div>
                            </div>

                            <div className="form-group">
                                <label className="form-label">Select File Type</label>
                                <select
                                    name="attachment_type"
                                    className="form-select-dropdown"
                                    value={state.attachment_type}
                                    onChange={handleInputChange}
                                    required
                                >
                                    <option value="">Select File Type</option>
                                    <option value="video">Video</option>
                                    <option value="pdf">PDF</option>
                                </select>
                            </div>

                            <div className="form-group">
                                <label className="form-label">File</label>
                                {(state?.file_name == "") ? (
                                    <label
                                        onChange={handleFileChange}
                                        className="file-upload-label">
                                        <input
                                            type="file"
                                            className="form-input create-content-input"
                                            accept={state.attachment_type === 'pdf' ? '.pdf' : state.attachment_type === 'video' ? 'video/*' : ''}
                                            disabled={!state.attachment_type}
                                            required
                                            style={{ display: "none" }}
                                        />
                                        Choose File
                                    </label>
                                ) : (
                                    <p className='attachment-para '>
                                        {state?.file_name}
                                        <button type="button" onClick={handleDeleteFile} className='delete-button-addcontent'><FaTrash size={16} color="red" style={{ opacity: 0.5 }} /></button>
                                    </p>
                                )}
                            </div>


                            <div className="button-group">
                                <button
                                    type="button"
                                    className="button-close"
                                    onClick={() => navigate(contentListPath)}
                                    disabled={submitting}
                                >
                                    Close
                                </button>
                                <button
                                    type="submit"
                                    className="button-send"
                                    onClick={id ? handleOnUpdateClick : handleOnSaveClick}
                                    disabled={!isFormValid() || submitting}
                                >
                                    {submitting ? "Saving..." : id ? "Update" : "Save"}
                                </button>
                            </div>
                        </form>
                    </div>}
                </div>
            </div>
        </div>
    );
};

export default AddContent;
